// Thin fetch wrapper around the WxCC MCP endpoints.
//
// Two modes, picked at module-load time:
//
//   Dev-proxy mode  — REACT_APP_CCUTILS_API_BASE_URL is NOT set. Calls go
//                     to /api/mcp/ops and /api/mcp/cc; src/setupProxy.js
//                     intercepts them in the CRA dev server, injects the
//                     WxCC OAuth bearer from the local .env, and forwards
//                     to the Cisco MCP endpoints. Browser never sees the
//                     token.
//
//   Lambda-proxy    — REACT_APP_CCUTILS_API_BASE_URL IS set (every
//                     deployed build). All calls go to POST ${API_BASE}
//                     /ccutils/wxcc-mcp with { endpoint, body } — the
//                     wxcc-mcp-proxy Lambda forwards to Cisco MCP using
//                     the token stored in its env (or Secrets Manager
//                     once migrated). API Gateway uses the CC Utils
//                     CUSTOM authorizer, so this mode requires an MSAL
//                     Bearer — register the token-getter from the top-
//                     level component via setMcpAuthTokenGetter().
//
// The dev-proxy fallback stays live so local dev with .env credentials
// keeps working without needing the Lambda deployed.

const API_BASE = process.env.REACT_APP_CCUTILS_API_BASE_URL || '';
const USE_LAMBDA_PROXY = !!API_BASE;

const DEV_PROXY_ENDPOINT = { ops: '/api/mcp/ops', cc: '/api/mcp/cc' };
const LAMBDA_PROXY_URL = USE_LAMBDA_PROXY ? `${API_BASE}/ccutils/wxcc-mcp` : null;

let sessionIds = { ops: null, cc: null };
let initialized = { ops: false, cc: false };
let rpcId = 0;

// Module-level MSAL token getter. The top-level WxccAiOps component is
// responsible for calling setMcpAuthTokenGetter(useAuth().getApiToken)
// so Lambda-proxy mode can attach the Bearer.
let apiTokenGetter = null;

export function setMcpAuthTokenGetter(getter) {
  apiTokenGetter = typeof getter === 'function' ? getter : null;
}

async function rpc(endpoint, method, params) {
  rpcId += 1;
  const id = rpcId;
  const body = { jsonrpc: '2.0', id, method, params };

  const headers = { 'Content-Type': 'application/json' };
  if (sessionIds[endpoint]) headers['Mcp-Session-Id'] = sessionIds[endpoint];

  let url;
  let fetchBody;
  if (USE_LAMBDA_PROXY) {
    url = LAMBDA_PROXY_URL;
    // Lambda proxy wraps the JSON-RPC envelope so one route serves both
    // endpoints. Session ID also travels in the body as a fallback in
    // case API Gateway strips the custom request header.
    fetchBody = JSON.stringify({ endpoint, body, sessionId: sessionIds[endpoint] });
    if (apiTokenGetter) {
      try {
        const token = await apiTokenGetter();
        if (token) headers['Authorization'] = `Bearer ${token}`;
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('MCP: failed to acquire MSAL token:', e?.message || e);
      }
    }
  } else {
    url = DEV_PROXY_ENDPOINT[endpoint];
    fetchBody = JSON.stringify(body);
  }

  const res = await fetch(url, { method: 'POST', headers, body: fetchBody });

  const sid = res.headers.get('mcp-session-id');
  if (sid) sessionIds[endpoint] = sid;

  const contentType = res.headers.get('content-type') || '';
  const text = await res.text();
  const payload = contentType.includes('text/event-stream') ? parseSseFirstMessage(text) : safeJsonParse(text);

  if (!res.ok) {
    const msg = payload?.error?.message || text.slice(0, 300);
    throw new Error(`MCP ${method} HTTP ${res.status}: ${msg}`);
  }
  if (!payload) throw new Error(`MCP ${method}: unparseable response`);
  if (payload.error) throw new Error(`MCP ${method} error ${payload.error.code}: ${payload.error.message}`);
  return payload.result;
}

async function ensureInit(endpoint) {
  if (initialized[endpoint]) return;
  await rpc(endpoint, 'initialize', {
    protocolVersion: '2025-03-26',
    capabilities: {},
    clientInfo: { name: 'wxcc-operations-center', version: '0.1.0' },
  });
  initialized[endpoint] = true;
}

export async function callTool(endpoint, name, args) {
  await ensureInit(endpoint);
  const result = await rpc(endpoint, 'tools/call', { name, arguments: args });
  const content = result?.content;
  if (Array.isArray(content) && content[0]?.type === 'text' && typeof content[0].text === 'string') {
    try {
      return JSON.parse(content[0].text);
    } catch {
      return content[0].text;
    }
  }
  return result;
}

export async function listTools(endpoint) {
  await ensureInit(endpoint);
  return rpc(endpoint, 'tools/list', {});
}

function parseSseFirstMessage(sseText) {
  for (const line of sseText.split(/\r?\n/)) {
    if (line.startsWith('data:')) {
      try {
        return JSON.parse(line.slice(5).trim());
      } catch {
        // continue
      }
    }
  }
  return null;
}

function safeJsonParse(text) {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}
